import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Divider, Radio } from 'antd';

import TreasurySharesAcquisitionForm from '../components/treasurySharesAcquisitionForm';
import ProfitCapitalizationForm from '../components/profitCapitalizationForm';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ComponentHeader from 'components/componentHeader/componentHeader';
import BackButton from 'components/backButton/backButton';
import PageTitle from 'components/title/title';

import { onGetCompanyServices, onGetServiceCost } from 'modules/main/actions/commonServices';
import NotificationService from 'core/services/notification';

import { RaiseCapitalTypeKey } from '../models/raiseCapitals';
import { CompanyServiceKey, ServiceCost } from 'modules/main/actions/commonModels';
import PrivateEquityIssuanceForm from '../components/PrivateEquityForm';

const typeServiceKeys: { type: RaiseCapitalTypeKey; service: CompanyServiceKey }[] = [
	{ type: 'ProfitCapitalization', service: 'profit_capitalization' },
	{ type: 'TreasurySharesAcquisition', service: 'treasury_shares_acquisition' },
	{ type: 'PrivateEquityIssuance', service: 'private_equity_issuance' },
];

const RaiseCapitalsCreatePage: FunctionComponent = () => {
	const { t } = useTranslation();

	const navigate = useNavigate();

	const [selectedType, setSelectedType] = useState<RaiseCapitalTypeKey>();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [companyServices, setCompanyServices] = useState<CompanyServiceKey[]>([]);
	
	const [profitCapitalizationServiceCost, setProfitCapitalizationServiceCost] = useState<ServiceCost>();
	const [privateEquityIssuanceServiceCost, setPrivateEquityIssuanceServiceCost] = useState<ServiceCost>();

	useEffect(() => {
		getData();
	}, []);

	const getData = async () => {
		try {
			setIsLoading(true);

			const servicesResponse = await onGetCompanyServices();
			const services = servicesResponse?.services?.map((service) => service.key) ?? [];
			setCompanyServices(services);

			await Promise.all([
				services.includes('profit_capitalization') && getProfitCapitalizationServiceCost(),
				services.includes('private_equity_issuance') && getPrivateEquityIssuanceServiceCost(),
			]);

			const firstAvailableType = typeServiceKeys.find((item) => services.includes(item.service))?.type;
			setSelectedType(firstAvailableType);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR', t('COMMON.FETCH_ERROR')));
			navigate('/dashboard/actions/capitals');
		}
	};

	const getProfitCapitalizationServiceCost = async () => {
		const response = await onGetServiceCost('profit_capitalization');
		setProfitCapitalizationServiceCost(response?.success);
	};

	const getPrivateEquityIssuanceServiceCost = async () => {
		const response = await onGetServiceCost('private_equity_issuance');
		setPrivateEquityIssuanceServiceCost(response?.success);
	};

	const capitalTypeOptions = [
		{
			value: 'ProfitCapitalization',
			label: (
				<>
					<p className="mb-0">{t('ACTIONS.PROFIT_CAPITALIZATION')}</p>
					<p className="mb-0 text-light">{t('ACTIONS.PROFIT_CAPITALIZATION_DESC')}</p>
				</>
			),
		},
		{
			value: 'TreasurySharesAcquisition',
			label: (
				<>
					<p className="mb-0">{t('ACTIONS.TREASURY_SHARES_ACQUISITION')}</p>
					<p className="mb-0 text-light">{t('ACTIONS.TREASURY_SHARES_ACQUISITION_DESC')}</p>
				</>
			),
		},
		{
			value: 'PrivateEquityIssuance',
			label: (
				<>
					<p className="mb-0">{t('ACTIONS.PRIVATE_EQUITY_ISSUANCE')}</p>
					<p className="mb-0 text-light">{t('ACTIONS.PRIVATE_EQUITY_ISSUANCE_DESC')}</p>
				</>
			),
		},
	].filter((option) => companyServices.includes(typeServiceKeys.find((item) => item.type === option.value)!.service));

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Card>
					<ComponentHeader title={t('ACTIONS.RAISE_CAPITAL')} subtitle={t('ACTIONS.FILL_THE_FOLLOWING_TO_PROCESS_REQUEST')} />

					<h6 className="fw-medium">{t('ACTIONS.TYPE_OF_RAISE')}</h6>

					<Radio.Group
						className="customRadio"
						style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
						options={capitalTypeOptions}
						defaultValue={selectedType}
						onChange={(e) => setSelectedType(e.target.value)}
					/>

					<Divider />

					{selectedType === 'ProfitCapitalization' && <ProfitCapitalizationForm serviceCost={profitCapitalizationServiceCost} />}

					{selectedType === 'TreasurySharesAcquisition' && <TreasurySharesAcquisitionForm />}

					{selectedType === 'PrivateEquityIssuance' && <PrivateEquityIssuanceForm serviceCost={privateEquityIssuanceServiceCost} />}
				</Card>
			)}
		</>
	);
};

export default RaiseCapitalsCreatePage;
